import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { Formik, FormikHelpers } from "formik";
import { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  ToggleButton,
  ToggleButtonGroup
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";

import PageHeader from "../../components/PageHeader";
import CardLoader from "../../components/content-loader/CardLoader";
import PasswordHintOverlay from "../../components/overlay/PasswordHintOverlay";
import {
  EToastvariant,
  IAddEditUser,
  ITaxExpertForm,
  IUserResponse
} from "../../config/interfaces";

import TaxExpertsApi from "../../api/TaxExpertsApi";
import FASvgIcon from "../../components/FASvgIcon";
import ScrollToFirstError from "../../components/ScrollToFirstError";
import PhoneInputField from "../../components/fields/PhoneInputField";
import { ButtonSpinner } from "../../components/placeholders";
import { appConstants } from "../../config/app-constants";
import { useAppDispatch } from "../../redux/Store";
import { showToastMsg } from "../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../services";

const INIT_UF_VALUES: ITaxExpertForm = {
  firstName: "",
  middleName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  inactive: false,
  password: "",
  confirmPassword: ""
};

const INITIAL_FORM_SCHEMA = {
  firstName: Yup.string()
    .trim()
    .min(3, "Please enter at least 3 characters")
    .max(255, "First Name must be less than 255 characters")
    .matches(appConstants.NAME_REGEX, "Invalid First Name")
    .required("First Name is required"),
  middleName: Yup.string()
    .trim()
    .min(3, "Please enter at least 3 characters")
    .max(255, "First Name must be less than 255 characters")
    .matches(appConstants.NAME_REGEX, "Invalid First Name")
    .notRequired(),
  lastName: Yup.string()
    .trim()
    .min(3, "Please enter at least 3 characters")
    .max(255, "Last Name must be less than 255 characters")
    .matches(appConstants.NAME_REGEX, "Invalid Last Name")
    .required("Last Name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .trim()
    .matches(appConstants.EMAIL_REGEX, "Invalid email address")
    .required("Email is required"),
  phoneNumber: Yup.string()
    .trim()
    .min(6, "Invalid phone number")
    .max(15, "Invalid phone number")
    .notRequired(),
  inactive: Yup.boolean(),
  password: Yup.string()
    .trim()
    .matches(appConstants.PASSWORD_REGEX, "Invalid Password")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .trim()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Repeat new password is required")
};

const TaxExpertAdd = () => {
  const { uUId } = useParams();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const pwdOverlayTargetRef = useRef<HTMLInputElement | null>(null);
  const [showHintOverlay, setShowHintOverlay] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState(true);
  const [initialFormValues, setInitialFormValues] = useState(INIT_UF_VALUES);
  const [initialFormSchema, setInitialFormSchema] = useState(
    Yup.object().shape(INITIAL_FORM_SCHEMA)
  );

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const toggleShowConfirmPwd = () => {
    setShowConfirmPwd(!showConfirmPwd);
  };

  useEffect(() => {
    getTaxExpertDetails();
  }, []);

  const getTaxExpertDetails = async () => {
    try {
      if (!uUId) {
        return;
      }
      setIsFetching(true);
      const taxExpertsApi = TaxExpertsApi.getInstance();
      const response = await taxExpertsApi.getTaxExpertById(uUId);
      const _data = response;
      const _taxExpertData = {
        firstName: _data?.firstName || "",
        middleName: _data?.middleName || "",
        lastName: _data?.lastName || "",
        email: _data?.email || "",
        phoneNumber: _data?.phoneNumber || "",
        inactive: _data?.inactive || false,
        password: "",
        confirmPassword: ""
      };
      setInitialFormValues(() => _taxExpertData);
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsFetching(false);
    }
  };

  const handleAddEditFormSubmit = async (
    values: ITaxExpertForm,
    { setSubmitting, resetForm }: FormikHelpers<ITaxExpertForm>
  ) => {
    try {
      setSubmitting(true);

      const fullName = `${values?.firstName || ""} ${values?.lastName || ""}`;

      let _msg = `${fullName} added successfully`;
      let _uUId = uUId;
      const _values: IAddEditUser = {
        firstName: values?.firstName,
        middleName: values?.middleName,
        lastName: values?.lastName,
        email: values.email.toLowerCase(),
        phoneNumber: values?.phoneNumber,
        inactive: values?.inactive || false,
        password: values?.password,
        role: "ca"
      };
      let responseData: IUserResponse | null = null;
      const taxExpertsApi = TaxExpertsApi.getInstance();
      if (_uUId) {
        responseData = await taxExpertsApi.updateTaxExpertById(_uUId, _values);
        _msg = `Changes applied to ${fullName} successfully`;
      } else {
        responseData = await taxExpertsApi.addTaxExpert(_values);
        _uUId = responseData?.uuid;
        resetForm();
      }

      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Success",
          message: _msg,
          variant: EToastvariant.Success
        })
      );

      navigate(`/tax-experts`);
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Container className="w-800">
        <PageHeader backIconButton={true} title="Add Tax Expert" />

        {isFetching ? (
          <CardLoader cardType="details" />
        ) : (
          <Formik
            enableReinitialize={true}
            initialValues={initialFormValues}
            validationSchema={initialFormSchema}
            onSubmit={handleAddEditFormSubmit}
          >
            {({
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
              validateForm,
              values,
              isValid,
              errors,
              touched,
              isSubmitting,
              submitCount,
              setTouched
            }) => (
              <>
                <Form
                  onSubmit={async (event) => {
                    event.preventDefault();
                    await validateForm();
                    handleSubmit();
                  }}
                >
                  <ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />
                  <Card className="form-card">
                    <Card.Body>
                      <Row className="align-items-center">
                        <Col sm={3}>
                          <Form.Label className="mb-0">Name</Form.Label>
                        </Col>
                        <Col>
                          <Row className="align-items-center">
                            <Col sm={4}>
                              <Form.Group className="form-group" controlId="firstName">
                                <Form.Label className="small-label required-field">
                                  First Name
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  autoComplete="false"
                                  placeholder="First Name"
                                  maxLength={255}
                                  name="firstName"
                                  value={values.firstName}
                                  onBlur={handleBlur("firstName")}
                                  onChange={handleChange("firstName")}
                                  isInvalid={Boolean(errors.firstName) && touched.firstName}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.firstName}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            <Col sm={4}>
                              <Form.Group className="form-group" controlId="middleName">
                                <Form.Label className="small-label">Middle Name</Form.Label>
                                <Form.Control
                                  type="text"
                                  autoComplete="false"
                                  placeholder="Middle Name"
                                  maxLength={255}
                                  name="middleName"
                                  value={values.middleName}
                                  onBlur={handleBlur("middleName")}
                                  onChange={handleChange("middleName")}
                                  isInvalid={Boolean(errors.middleName) && touched.middleName}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.middleName}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            <Col sm={4}>
                              <Form.Group className="form-group" controlId="lastName">
                                <Form.Label className="small-label required-field">
                                  Last Name
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  autoComplete="false"
                                  placeholder="Last Name"
                                  maxLength={255}
                                  name="lastName"
                                  value={values.lastName}
                                  onBlur={handleBlur("lastName")}
                                  onChange={handleChange("lastName")}
                                  isInvalid={Boolean(errors.lastName) && touched.lastName}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.lastName}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                      <Row className="align-items-center">
                        <Col sm={3}>
                          <Form.Label className="required-field inline-label">Email</Form.Label>
                        </Col>
                        <Col>
                          <Form.Group className="form-group" controlId="email">
                            <Form.Control
                              type="text"
                              autoComplete="false"
                              placeholder="Email"
                              maxLength={255}
                              name="email"
                              value={values.email}
                              onBlur={handleBlur("email")}
                              onChange={handleChange}
                              isInvalid={Boolean(errors.email) && touched.email}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.email}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row className="align-items-center">
                        <Col sm={3}>
                          <Form.Label className="inline-label">Phone</Form.Label>
                        </Col>
                        <Col>
                          <PhoneInputField
                            name="phoneNumber"
                            value={values.phoneNumber}
                            onChange={(value) => {
                              setFieldValue("phoneNumber", value);
                            }}
                            onBlur={handleBlur("phoneNumber")}
                            isInvalid={Boolean(errors.phoneNumber) && touched.phoneNumber}
                            errorMsg={errors.phoneNumber}
                            formType="GENERAL"
                          />
                        </Col>
                      </Row>
                      <Row className="align-items-center">
                        <Col sm={3}>
                          <Form.Label className="required-field inline-label">Status</Form.Label>
                        </Col>
                        <Col>
                          <ToggleButtonGroup
                            type="radio"
                            name="inactive"
                            value={values.inactive ? "Inactive" : "Active"}
                            onChange={(selectValue) => {
                              const newValue = selectValue === "Inactive"; // Convert "Inactive" to true, "Active" to false
                              setFieldValue("inactive", newValue);
                            }}
                            className="rb-group-control"
                          >
                            <ToggleButton
                              variant="outline-success"
                              id="active"
                              value="Active"
                              className="btn-w-110 btn-h-40"
                            >
                              Active
                            </ToggleButton>
                            <ToggleButton
                              variant="outline-danger"
                              id="inactive"
                              value="Inactive"
                              className="btn-w-110 btn-h-40"
                            >
                              Inactive
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Col>
                      </Row>
                      <Row className="align-items-center">
                        <Col sm={3}>
                          <Form.Label className="required-field inline-label">Password</Form.Label>
                        </Col>
                        <Col>
                          <Form.Group className="form-group" controlId="password">
                            <div className="field-has-icon" ref={pwdOverlayTargetRef}>
                              <Form.Control
                                type={showPassword ? "text" : "password"}
                                name="password"
                                isInvalid={!!errors.password && !!touched.password}
                                value={values.password}
                                placeholder="***********"
                                onChange={handleChange}
                                onFocus={() => {
                                  setShowHintOverlay(true);
                                }}
                                onBlur={(e) => {
                                  handleBlur(e);
                                  setShowHintOverlay(false);
                                }}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.password}
                              </Form.Control.Feedback>
                              <Button
                                variant="btn"
                                className="field-icon"
                                onClick={toggleShowPassword}
                              >
                                <FASvgIcon icon={showPassword ? faEye : faEyeSlash} />
                              </Button>
                            </div>
                            <PasswordHintOverlay
                              show={showHintOverlay}
                              targetRef={pwdOverlayTargetRef}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row className="align-items-center">
                        <Col sm={3}>
                          <Form.Label className="required-field inline-label">
                            Repeat Password
                          </Form.Label>
                        </Col>
                        <Col>
                          <Form.Group className="form-group" controlId="confirmPassword">
                            <div className="field-has-icon">
                              <Form.Control
                                type={showConfirmPwd ? "text" : "password"}
                                name="confirmPassword"
                                onBlur={handleBlur}
                                isInvalid={!!errors.confirmPassword && !!touched.confirmPassword}
                                value={values.confirmPassword}
                                placeholder="***********"
                                onChange={handleChange}
                                autoComplete="confirmPassword"
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.confirmPassword}
                              </Form.Control.Feedback>
                              <Button
                                variant="btn"
                                className="field-icon"
                                onClick={toggleShowConfirmPwd}
                              >
                                <FASvgIcon icon={showConfirmPwd ? faEye : faEyeSlash} />
                              </Button>
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>

                      <div className="form-btns-wrap">
                        <Button
                          size="lg"
                          variant="outline-greyDark"
                          className="btn-w-120"
                          onClick={() => {
                            navigate("/tax-experts");
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="lg"
                          variant="primary"
                          className="btn-w-160"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <ButtonSpinner /> : "Save"}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Form>
              </>
            )}
          </Formik>
        )}
      </Container>
    </>
  );
};

export default TaxExpertAdd;
